import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { getFeedbackForm, getProfileForm } from "../../../../redux/reducer/incbotFeedbackReducer";
import { Card } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { Label } from "../../../ui/label";
import { Input } from "../../../ui/input";
import { Clock, CheckCircle, XCircle } from "lucide-react";
import { formatDuration } from "../../../../utils/helper";

const SKIPPED_ANSWER_COPY = "The candidate skipped the question.";

export type PsychometricFeedbackRow = {
  _id?: string;
  questionType?: string;
  question?: string;
  role?: string;
  candidate_answer?: string;
  skill?: string;
  sub_skill?: string;
  answerAudioUrl?: string | null;
  transcript?: string | null;
  submittedAt?: string;
  timeTaken?: number;
  time_taken?: number;
};

type BankRow = {
  _id?: string;
  question?: string;
  type?: string;
  level?: string;
  options?: Record<string, string>;
  skill?: string;
  sub_skill?: string;
  predefinedAnswer?: string;
  correctAnswer?: string;
  answer?: string;
};

/** One row after merging interview question bank + feedback answers. */
export type MergedPsychometricRow = {
  question: string;
  questionType: string;
  skill?: string;
  sub_skill?: string;
  candidate_answer: string;
  transcript: string | null | undefined;
  answerAudioUrl: string | null | undefined;
  submittedAt?: string;
  /** Seconds (from API or derived); drives `12s` next to transcript like `SpecificQuestion`. */
  timeTaken?: number;
  options?: Record<string, string>;
  bankRow: BankRow;
  skipped: boolean;
};

function normQuestion(s: unknown): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function findFeedbackForBank(
  bankRow: BankRow,
  feedback: PsychometricFeedbackRow[]
): PsychometricFeedbackRow | undefined {
  const bid = String(bankRow._id ?? "").trim();
  if (bid) {
    const byId = feedback.find((f) => String(f._id ?? "").trim() === bid);
    if (byId) return byId;
  }
  const key = normQuestion(bankRow.question);
  if (!key) return undefined;
  return feedback.find((f) => normQuestion(f.question) === key);
}

function findBankMatch(bank: BankRow[], questionText: string): BankRow | undefined {
  const key = normQuestion(questionText);
  return bank.find((b) => normQuestion(b?.question) === key);
}

function bankLevelLabel(b: BankRow): string {
  const ext = b as BankRow & { Level?: string };
  return String(b.level ?? ext.Level ?? "").trim();
}

function sectionLevelsLabel(responses: MergedPsychometricRow[]): string {
  const levels = Array.from(
    new Set(responses.map((r) => bankLevelLabel(r.bankRow)).filter((s) => s.length > 0))
  );
  if (levels.length === 0) return "Not specified";
  if (levels.length === 1) return levels[0]!;
  return levels.join(" · ");
}

function feedbackTimeTakenSeconds(fb?: PsychometricFeedbackRow): number | undefined {
  if (!fb) return undefined;
  if (typeof fb.timeTaken === "number" && Number.isFinite(fb.timeTaken)) {
    return Math.max(0, Math.round(fb.timeTaken));
  }
  if (typeof fb.time_taken === "number" && Number.isFinite(fb.time_taken)) {
    return Math.max(0, Math.round(fb.time_taken));
  }
  return undefined;
}

function mergePsychometricData(
  bank: BankRow[],
  feedback: PsychometricFeedbackRow[]
): MergedPsychometricRow[] {
  if (bank.length > 0) {
    return bank
      .filter((b) => String(b.question ?? "").trim())
      .map((bankRow) => {
        const fb = findFeedbackForBank(bankRow, feedback);
        const questionType = String(fb?.questionType ?? bankRow.type ?? "").toLowerCase();
        const rawAnswer = String(fb?.candidate_answer ?? "").trim();
        const skipped = rawAnswer.length === 0;
        const apiSec = feedbackTimeTakenSeconds(fb);
        return {
          question: String(bankRow.question ?? fb?.question ?? ""),
          questionType,
          skill: fb?.skill ?? bankRow.skill,
          sub_skill: fb?.sub_skill ?? bankRow.sub_skill,
          candidate_answer: rawAnswer,
          transcript: fb?.transcript,
          answerAudioUrl: fb?.answerAudioUrl ?? null,
          submittedAt: fb?.submittedAt,
          timeTaken: apiSec,
          options: bankRow.options,
          bankRow,
          skipped,
        };
      });
  }

  return feedback
    .filter((r) => String(r.question ?? "").trim())
    .map((r) => {
      const match = findBankMatch(bank, String(r.question ?? ""));
      const rawAnswer = String(r.candidate_answer ?? "").trim();
      const skipped = rawAnswer.length === 0;
      const bankRow = match ?? { question: r.question };
      const apiSec = feedbackTimeTakenSeconds(r);
      return {
        question: String(r.question ?? ""),
        questionType: String(r.questionType ?? "").toLowerCase(),
        skill: r.skill,
        sub_skill: r.sub_skill,
        candidate_answer: rawAnswer,
        transcript: r.transcript,
        answerAudioUrl: r.answerAudioUrl,
        submittedAt: r.submittedAt,
        timeTaken: apiSec,
        options: match?.options,
        bankRow,
        skipped,
      };
    });
}

/** Seconds next to transcript: API `timeTaken`, else delta from previous subjective / interview start. */
function subjectiveTranscriptSeconds(
  q: MergedPsychometricRow,
  prev: MergedPsychometricRow | undefined,
  interviewStartMs: number | null
): number | null {
  if (typeof q.timeTaken === "number" && Number.isFinite(q.timeTaken)) {
    return Math.max(0, Math.round(q.timeTaken));
  }
  const cur = q.submittedAt ? new Date(q.submittedAt).getTime() : NaN;
  if (!Number.isFinite(cur)) return null;

  if (prev?.submittedAt) {
    const prevT = new Date(prev.submittedAt).getTime();
    if (Number.isFinite(prevT)) {
      return Math.max(0, Math.round((cur - prevT) / 1000));
    }
  }

  if (interviewStartMs != null && Number.isFinite(interviewStartMs)) {
    return Math.max(0, Math.round((cur - interviewStartMs) / 1000));
  }

  return 0;
}

function optionLetterLabel(letter: string, options?: Record<string, string>): string {
  const L = letter.trim().toUpperCase();
  if (!L) return "";
  const key = L.toLowerCase();
  const text = options?.[key];
  return text ? `${L}: ${text}` : L;
}

/** Same option list wrapper + rows as `AptitudeSection` (one block per option line). */
function renderMcqOptionLines(options?: Record<string, string>) {
  if (!options || typeof options !== "object") return null;
  const order = ["a", "b", "c", "d", "e", "f"];
  const lines = order
    .map((k) => {
      const v = options[k];
      return v ? `${k.toUpperCase()}. ${String(v)}` : null;
    })
    .filter(Boolean) as string[];
  if (lines.length === 0) return null;
  return (
    <div
      className="space-y-2 text-gray-700 text-sm lg:text-base break-words whitespace-pre-wrap"
      style={{ wordBreak: "break-word" }}
    >
      {lines.map((line, i) => (
        <div key={i} className="break-words whitespace-pre-wrap">
          {line}
        </div>
      ))}
    </div>
  );
}

function bankPredefinedAnswer(match?: BankRow): string {
  if (!match) return "";
  const m = match as BankRow & {
    predefined_answer?: string;
    correct_answer?: string;
  };
  return (
    m.predefinedAnswer ||
    m.correctAnswer ||
    m.answer ||
    m.predefined_answer ||
    m.correct_answer ||
    ""
  ).trim();
}

/** Extract A–D (or first letter) for comparison with candidate letter. */
function extractChoiceLetter(value: string): string {
  const t = value.trim().toUpperCase();
  if (/^[A-Z]$/.test(t)) return t;
  const m = t.match(/\b([A-Z])\b/);
  return m ? m[1] : "";
}

function mcqRating(hasAnswer: boolean, rawAnswer: string, predefined: string): number {
  if (!hasAnswer) return 0;
  const got = extractChoiceLetter(rawAnswer);
  const exp = extractChoiceLetter(predefined);
  if (!got || !exp) return 0;
  return got === exp ? 1 : 0;
}

const PsychometricScreeningSection: React.FC = () => {
  const feedbackForm = useSelector(getFeedbackForm);
  const profileForm = useSelector(getProfileForm);

  const interviewStartMs = useMemo(() => {
    const iso = profileForm?.interviewDate;
    if (!iso) return null;
    const t = new Date(String(iso)).getTime();
    return Number.isFinite(t) ? t : null;
  }, [profileForm?.interviewDate]);

  const mergedRows = useMemo(() => {
    const rows = (feedbackForm?.psychometricQuestionsFeedback ?? []) as PsychometricFeedbackRow[];
    const questionBank = (feedbackForm?.psychometricQuestionBank ?? []) as BankRow[];
    return mergePsychometricData(questionBank, rows);
  }, [feedbackForm]);

  const mcq_evaluations = useMemo(() => {
    return feedbackForm?.psychometricSkillWise.mcq_evaluations ?? [];
  }, [feedbackForm]);

  const subjective = mergedRows.filter((r) => r.questionType === "subjective");
  const optionRows = mergedRows.filter((r) => r.questionType === "option");

  const subjectiveSections = useMemo(() => {
    const map = new Map<string, MergedPsychometricRow[]>();
    subjective.forEach((r) => {
      const key = String(r.skill ?? "").trim() || "Psychometric Subjective";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    return Array.from(map.entries());
  }, [subjective]);

  const mcqSections = useMemo(() => {
    const map = new Map<string, MergedPsychometricRow[]>();
    optionRows.forEach((r) => {
      const key = String(r.skill ?? "").trim() || "Psychometric MCQ";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    return Array.from(map.entries());
  }, [optionRows]);

  if (mergedRows.length === 0) {
    return (
      <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
        <p className="text-gray-600 text-sm md:text-base">
          No psychometric screening responses are available for this interview.
        </p>
      </div>
    );
  }

  return (
    <>
      {subjective.length > 0 ? (
        <>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm p-4 md:p-6 mb-3 md:mb-6 border border-blue-100">
            <h1 className="text-lg md:text-xl lg:text-2xl font-semibold text-gray-900 mb-1 md:mb-2">
              Subjective Questions
            </h1>
            <p className="text-gray-600 text-xs md:text-sm lg:text-base">
              Evaluation of the candidate&apos;s knowledge and suitability for the targeted role.
            </p>
          </div>
          <div className="mb-3 md:mb-6">
            {subjectiveSections.map(([sectionTitle, responses], sectionIndex) => {
              const lastMs = responses.reduce((acc, r) => {
                if (!r.submittedAt) return acc;
                const t = new Date(r.submittedAt).getTime();
                return Number.isFinite(t) ? Math.max(acc, t) : acc;
              }, 0);
              const firstMs = responses.reduce((acc, r) => {
                if (!r.submittedAt) return acc;
                const t = new Date(r.submittedAt).getTime();
                if (!Number.isFinite(t)) return acc;
                if (acc === 0) return t;
                return Math.min(acc, t);
              }, 0);
              const spanSec =
                lastMs > 0 && firstMs > 0 ? Math.max(0, Math.round((lastMs - firstMs) / 1000)) : 0;
              const difficultyLabel = sectionLevelsLabel(responses);

              return (
                <div
                  key={`psych-sub-sec-${sectionTitle}`}
                  className={sectionIndex < subjectiveSections.length - 1 ? "mb-8" : ""}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4 mb-4 md:mb-6">
                    <h2 className="text-md lg:text-xl font-medium text-gray-900">{sectionTitle}</h2>
                    <div className="flex flex-wrap items-center gap-2 lg:gap-3">
                      <Badge
                        variant="outline"
                        className="bg-cyan-50 text-cyan-700 border-cyan-200 text-xs lg:text-sm"
                      >
                        📝 Questions: {responses.length || 0}
                      </Badge>
                      <Badge
                        variant="outline"
                        className="bg-blue-50 text-blue-700 border-blue-200 text-xs lg:text-sm"
                      >
                        🎯 Difficulty Level: {difficultyLabel}
                      </Badge>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Clock size={14} className="lg:hidden" />
                        <Clock size={16} className="hidden lg:block" />
                        <span className="text-xs lg:text-sm">{formatDuration(spanSec)}</span>
                      </div>
                    </div>
                  </div>

                  {responses.map((q, questionIndex) => {
                    const body = q.skipped
                      ? SKIPPED_ANSWER_COPY
                      : (q.transcript && String(q.transcript).trim()) || q.candidate_answer || "—";
                    const prevInSection =
                      questionIndex > 0
                        ? responses[questionIndex - 1]
                        : sectionIndex > 0
                          ? (() => {
                              const prevList = subjectiveSections[sectionIndex - 1]?.[1];
                              return prevList && prevList.length > 0
                                ? prevList[prevList.length - 1]
                                : undefined;
                            })()
                          : undefined;
                    const transcriptSec = subjectiveTranscriptSeconds(
                      q,
                      prevInSection,
                      interviewStartMs
                    );
                    return (
                      <Card
                        key={`psych-sub-${sectionTitle}-${questionIndex}-${normQuestion(q.question)}`}
                        className={
                          questionIndex < responses.length - 1
                            ? "rounded-lg shadow-sm mb-4 md:mb-6"
                            : "rounded-lg shadow-sm"
                        }
                      >
                        <div className="p-3 md:p-4 lg:p-6 bg-white">
                          <div className="flex items-start justify-between mb-3 gap-2">
                            <div className="flex items-start gap-2 min-w-0">
                              <span className="text-xs md:text-lg font-medium shrink-0">
                                {questionIndex + 1}.
                              </span>
                              <div className="min-w-0">
                                <span className="text-xs md:text-lg whitespace-pre-wrap text-gray-900 block">
                                  {q.question}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="mb-2 md:mb-4">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className="text-xs md:text-sm font-medium text-gray-700">
                                Transcript:
                              </span>
                              <div className="flex items-center gap-1">
                                <Clock size={12} />
                                <span className="text-xs text-gray-500">
                                  {transcriptSec !== null ? `${transcriptSec}s` : "0s"}
                                </span>
                              </div>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs md:text-sm text-gray-700 whitespace-pre-wrap break-words">
                              {body}
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {optionRows.length > 0 ? (
        <>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm p-6 mb-6 border border-blue-100">
            <h1 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-2">MCQ Questions</h1>
            <p className="text-gray-600 text-sm lg:text-base">
              Insights into the candidate&apos;s analytical thinking and reasoning skills.
            </p>
          </div>

          {mcqSections.map(([sectionTitle, responses], sectionIndex) => {
            const totalMarks = responses.reduce((acc, q) => {
              const qText = String(q.question ?? "").trim();
              const evalEntry = mcq_evaluations.find((e: any) => {
                const eQText = String(e.question ?? "").trim();
                return qText && eQText && qText === eQText;
              });
              return acc + (evalEntry?.is_correct ? 1 : 0);
            }, 0);
            const lastMs = responses.reduce((acc, r) => {
              if (!r.submittedAt) return acc;
              const t = new Date(r.submittedAt).getTime();
              return Number.isFinite(t) ? Math.max(acc, t) : acc;
            }, 0);
            const firstMs = responses.reduce((acc, r) => {
              if (!r.submittedAt) return acc;
              const t = new Date(r.submittedAt).getTime();
              if (!Number.isFinite(t)) return acc;
              if (acc === 0) return t;
              return Math.min(acc, t);
            }, 0);
            const spanSec =
              lastMs > 0 && firstMs > 0 ? Math.max(0, Math.round((lastMs - firstMs) / 1000)) : 0;
            const mcqDifficultyLabel = sectionLevelsLabel(responses);

            return (
              <div
                key={sectionTitle}
                className={sectionIndex < mcqSections.length - 1 ? "mb-8" : ""}
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4 mb-6">
                  <h2 className="text-md lg:text-xl font-medium text-gray-900">{sectionTitle}</h2>
                  <div className="flex flex-wrap items-center gap-2 lg:gap-3">
                    <Badge
                      variant="outline"
                      className="bg-cyan-50 text-cyan-700 border-cyan-200 text-xs lg:text-sm"
                    >
                      📊 Marks Scored: {totalMarks} out of {responses.length || 0}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="bg-blue-50 text-blue-700 border-blue-200 text-xs lg:text-sm"
                    >
                      🎯 Difficulty Level: {mcqDifficultyLabel}
                    </Badge>
                    <div className="flex items-center gap-1 text-gray-600">
                      <Clock size={14} className="lg:hidden" />
                      <Clock size={16} className="hidden lg:block" />
                      <span className="text-xs lg:text-sm">{formatDuration(spanSec)}</span>
                    </div>
                  </div>
                </div>

                {responses.map((q, questionIndex) => {
                  const opts = q.options ?? q.bankRow?.options;
                  const rawAnswer = q.candidate_answer;
                  const hasAnswer = !q.skipped;
                  const predefined = bankPredefinedAnswer(q.bankRow);
                  const rating = mcqRating(hasAnswer, rawAnswer, predefined);

                  const is_correct = mcq_evaluations.find((e: any) => {
                    const qText = String(q.question ?? "").trim();
                    const eQText = String(e.question ?? "").trim();
                    return qText && eQText && qText === eQText;
                  })?.is_correct;

                  const correct_answer = mcq_evaluations.find((e: any) => {
                    const qText = String(q.question ?? "").trim();
                    const eQText = String(e.question ?? "").trim();
                    return qText && eQText && qText === eQText;
                  })?.correct_answer;

                  const userDisplay = hasAnswer
                    ? optionLetterLabel(rawAnswer, opts) || rawAnswer
                    : SKIPPED_ANSWER_COPY;
                  const correctDisplay = predefined
                    ? optionLetterLabel(predefined, opts) || predefined
                    : correct_answer
                      ? correct_answer
                      : "Not available";
                  const rowLevel = bankLevelLabel(q.bankRow);

                  return (
                    <Card
                      key={`psych-opt-${sectionTitle}-${questionIndex}-${normQuestion(q.question)}`}
                      className={
                        questionIndex < responses.length - 1
                          ? "rounded-lg shadow-sm mb-4"
                          : "rounded-lg shadow-sm"
                      }
                    >
                      <div className="bg-white rounded-lg p-4 lg:p-6">
                        <div className="flex items-start gap-3 lg:gap-4">
                          <span className="text-lg font-medium text-gray-700 mt-1 flex-shrink-0">
                            {questionIndex + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4 gap-3">
                              <div className="flex-1 lg:pr-4">
                                <p className="text-gray-900 mb-2 text-sm lg:text-base leading-relaxed break-words whitespace-pre-wrap">
                                  {q.question}
                                </p>
                                {renderMcqOptionLines(opts)}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {rating > 0 || is_correct ? (
                                  <>
                                    <CheckCircle className="text-green-500" size={18} />
                                    <Badge
                                      variant="outline"
                                      className="text-green-700 border-green-200 bg-green-50 text-xs lg:text-sm"
                                    >
                                      Correct
                                    </Badge>
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="text-red-500" size={18} />
                                    <Badge
                                      variant="outline"
                                      className="text-red-700 border-red-200 bg-red-50 text-xs lg:text-sm"
                                    >
                                      Incorrect
                                    </Badge>
                                  </>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                  User Input:
                                </Label>
                                <Input
                                  value={userDisplay}
                                  readOnly
                                  className={
                                    rating > 0 || is_correct
                                      ? "bg-green-50 border-green-200 text-sm"
                                      : "bg-red-50 border-red-200 text-sm"
                                  }
                                />
                              </div>
                              <div>
                                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                  Correct Answer:
                                </Label>
                                <Input
                                  // value={correctDisplay}
                                  value={String(correctDisplay).replace(
                                    /^\s*[a-f](?=\s*(?:[.:)]\s|$))/,
                                    (letter: string) => letter.toUpperCase()
                                  )}
                                  readOnly
                                  className="bg-blue-50 border-blue-200 text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            );
          })}
        </>
      ) : null}
    </>
  );
};

export default PsychometricScreeningSection;
